import { useEffect, useState, useRef } from "react";
import { useInView } from "framer-motion";
import { useTranslation } from "react-i18next";

const NUMBER_LOCALES = { en: "en-US", ar: "ar-JO", de: "de-DE", es: "es-ES", fr: "fr-FR" };

/**
 * AnimatedCounter — scroll-triggered number counter.
 * Accepts either a numeric value or a string with a numeric prefix like "4,500+", "$2M+", "80%".
 */
export function AnimatedCounter({ value = "0", duration = 1400, className = "" }) {
  const { i18n } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [display, setDisplay] = useState("0");
  const numberLocale = NUMBER_LOCALES[i18n.language] || NUMBER_LOCALES.en;

  useEffect(() => {
    if (!inView) return;
    // Parse leading number, preserve prefix/suffix (e.g., $, %, +, M, ",")
    const str = String(value);
    const match = str.match(/^([^\d-]*)([\d,.]+)(.*)$/);
    if (!match) {
      setDisplay(str);
      return;
    }
    const prefix = match[1];
    const rawNum = parseFloat(match[2].replace(/,/g, ""));
    const suffix = match[3];
    const isFloat = match[2].includes(".");
    const startTime = performance.now();
    const formatter = new Intl.NumberFormat(numberLocale, { maximumFractionDigits: isFloat ? 1 : 0 });

    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = rawNum * eased;
      setDisplay(prefix + formatter.format(isFloat ? current : Math.round(current)) + suffix);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(str);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration, numberLocale]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
